import React, { useMemo, useState } from 'react';
import {
  Article,
  CalendarBlank,
  Envelope,
  Funnel,
  Info,
  WarningCircle,
} from '@phosphor-icons/react';
import {
  DatePicker,
  Divider,
  Select,
  Table,
  Tooltip,
} from 'antd';
import axios from 'axios';
import moment from 'moment';
import Typography from '../../../components/Typography';
import COLORS from '../../../util/constants/colors';
import Button from '../../../components/Button';
import {
  includeReferredToProviderColumn,
  referralReportColumns,
  statusReportColumns,
} from './reportColumns';
import ReceivedTo from '../ReceivedTo';
import { buildOwnerParams } from '../helpers';
import LoadingAnimation from '../LoadingAnimation';
import { captureAnalytics } from '../../../actions/preload';

export const REPORT_TYPE_EXPLANATION = 'By Referral — One row per referral\nBy Status Change — One row per status update. The same referral may appear multiple times.';

const ReferralReportBuilder = ({ ownerContacts }) => {
  const [reportType, setReportType] = useState('');
  const [reportData, setReportData] = useState([]);
  const [reportKey, setReportKey] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, 'days').format('YYYY-MM-DD'),
    moment().format('YYYY-MM-DD')
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalResults, setTotalResults] = useState(0);

  const handleBuildReport = (newPage = 1, newPageSize = 10) => {
    setError(null);
    setLoading(true);
    const owners = buildOwnerParams(selectedRecipients, ownerContacts, true);
    axios({
      url: '/api/v1/referral_analytics/',
      method: 'POST',
      data: {
        start_date: dateRange[0] || moment().subtract(3, 'months').format('YYYY-MM-DD'),
        end_date: dateRange[1] || moment().format('YYYY-MM-DD'),
        owners,
        report_type: reportType,
        page: newPage,
        page_size: newPageSize,
      },
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
    }).then((response) => {
      setReportData(response.data.data)
      setTotalResults(reportType === 'referral' ? response.data.data.total_referrals : response.data.data.total_status_changes);
      setPage(newPage);
      setPageSize(newPageSize);
    }).catch((error) => {
      console.error(error);
      setError(error.response.data.error);
    }).finally(() => {
      setLoading(false);
    });
  };

  const emailReport = () => {
    setError(null);
    const owners = buildOwnerParams(selectedRecipients, ownerContacts, true);
    axios({
      url: '/api/v1/referral_analytics/export_report',
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
      },
      data: {
        start_date: dateRange[0],
        end_date: dateRange[1],
        report_type: reportType,
        owners,
      },
    }).then(() => {
      toastr.success('An email with your Referral Report will be sent shortly');
    }).catch((error) => {
      console.error(error);
      setError(error.response.data.error);
    });
  }

  const disabled = selectedRecipients.length === 0 || reportType === '';
  const referredToProviderEnabled = reportData.referred_to_provider_enabled === true;
  const reportColumns = useMemo(() => {
    return includeReferredToProviderColumn(
      reportType === 'referral' ? referralReportColumns : statusReportColumns,
      referredToProviderEnabled,
    );
  }, [referredToProviderEnabled, reportType]);

  return (
    <div className="referral-report-builder">
      <Typography variant="text" size="medium" bold className="referral-trends-header">
        <Funnel color={COLORS.BLUE_PRIMARY_LITE} size={16} weight="bold" />
        &nbsp;
        Referral Report Builder
      </Typography>
      <div className="report-inputs">
        <div className="report-input-item">
          <Typography variant="text" size="small" bold>
            <CalendarBlank size={16} weight="bold" color={COLORS.BLUE_PRIMARY_LITE} />
            &nbsp;
            Date Range
          </Typography>
          <DatePicker.RangePicker
            allowEmpty={[true, true]}
            value={dateRange.map((v) => v && moment(v, 'YYYY-MM-DD'))}
            onChange={(_, dateString) => {
              setDateRange(dateString);
            }}
            disabledDate={(current) => {
              // Disable dates beyond today
              if (current && current > moment()) {
                return true;
              }

              const [startDate, endDate] = dateRange;

              // If a start date is selected, disable dates that would create a range > 3 months
              if (startDate && moment(startDate).isValid()) {
                const start = moment(startDate, 'YYYY-MM-DD');
                // Disable dates more than 3 months from the start date
                if (current && (current < start.clone().subtract(3, 'months') || current > start.clone().add(3, 'months'))) {
                  return true;
                }
              }

              // If an end date is selected, disable dates that would create a range > 3 months
              if (endDate && moment(endDate).isValid()) {
                const end = moment(endDate, 'YYYY-MM-DD');
                // Disable dates more than 3 months from the end date
                if (current && (current < end.clone().subtract(3, 'months') || current > end.clone().add(3, 'months'))) {
                  return true;
                }
              }

              return false;
            }}
          />
        </div>
        <ReceivedTo
          ownerContacts={ownerContacts}
          selectedRecipients={selectedRecipients}
          setSelectedRecipients={setSelectedRecipients}
        />
        <div className="report-input-item">
          <Typography variant="text" size="small" bold>
            <Article size={16} weight="bold" color={COLORS.BLUE_PRIMARY_LITE} />
            &nbsp;
            Report Type
            &nbsp;
            <Tooltip
              title={REPORT_TYPE_EXPLANATION}
              overlayInnerStyle={{ whiteSpace: 'pre-line' }}
            >
              <span
                aria-label="Explain report types"
                role="button"
                tabIndex={0}
                onMouseEnter={() => captureAnalytics('referral_report_type_help_viewed')}
                onFocus={() => captureAnalytics('referral_report_type_help_viewed')}
              >
                <Info size={16} weight="bold" color={COLORS.BLUE_PRIMARY_LITE} />
              </span>
            </Tooltip>
          </Typography>
          <Select
            placeholder="Select Report Type"
            options={[
              { value: 'referral', label: 'By Referral' },
              { value: 'status', label: 'By Status' }
            ]}
            onChange={(value) => {
              setReportType(value);
              setReportKey(value === 'status' ? 'status_changes' : 'detailed_referrals');
            }}
          />
        </div>
        <div className="build-report-buttons">
          <Button
            type="primary"
            size="xsmall"
            label="Build Report"
            onClick={() => handleBuildReport()}
            disabled={disabled}
          />
          <Button
            type="secondary"
            size="xsmall"
            label="Email Report"
            leftIcon={<Envelope size={16} color={disabled ? COLORS.TEXT_DISABLED_LITE : COLORS.PURPLE_PRIMARY_LITE} />}
            onClick={() => emailReport()}
            disabled={disabled}
          />
        </div>
      </div>
      <Typography variant="text" size="small" color={COLORS.TEXT_TERTIARY_LITE}>
        <Info color={COLORS.TEXT_TERTIARY_LITE} size={16} weight="bold" />
        &nbsp;
        Select your filters and click "Build Report" to see your data.
      </Typography>
      <Divider style={{ backgroundColor: COLORS.BLUE, height: 1 }} />
      <div
        style={{
          width: `${window.innerWidth * 0.9 - 80}px`,
          margin: '0 auto',
          textWrap: 'nowrap',
          overflow: 'scroll',
        }}
      >
        {error && (
          <Typography variant="text" size="small" color={COLORS.ERROR_LITE}>
            <WarningCircle color={COLORS.ERROR_LITE} size={16} weight="bold" />
            &nbsp;
            {error}
          </Typography>
        )}
        {loading && (
          <LoadingAnimation />
        )}
        {reportData[reportKey] && (
          <Table
            columns={reportColumns}
            dataSource={reportData[reportKey]}
            loading={loading}
            pagination={{
              pageSize: pageSize,
              current: page,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50],
              showQuickJumper: false,
              total: totalResults,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              position: ['bottomCenter'],
            }}
            onChange={(pagination) => {
              handleBuildReport(pagination.current, pagination.pageSize);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ReferralReportBuilder;
